import React, { FC } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { ColumnSwitch } from "../../../../../../../redux/actions/dataTable/DataTableTypes";
import DataTableColumns from "../../../../columnsAndActions/ColumnsAndActions";

import {
  changeStatusColumn,
  disableAllColumns,
  selectAllColumns,
  setColumnsSearch,
} from "../../../../../../../redux/slices/dataTable/data";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../../../../../../redux/slices";
import { TFunction } from "i18next";
import { SwitchesStyles } from "../ColumnSwitchesStyles";

import AdjustIcon from "@mui/icons-material/Adjust";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import BiotechIcon from "@mui/icons-material/Biotech";
import { normalizeText } from "../../../../../../helpers/helpersSearchs";

export type FN = (arg: string) => string;
export type RFN<F extends FN> = Readonly<FN> & F;

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    changeStatus: (name: string) => {
      dispatch(changeStatusColumn(name));
    },

    setColumnSearch: (value: string) => {
      dispatch(setColumnsSearch(value));
    },

    disableAll: (value: string[]) => {
      dispatch(disableAllColumns(value));
    },
    selectAll: (value: string[]) => {
      dispatch(selectAllColumns(value));
    },
  };
};

type SwitchesParams = {
  t: TFunction<"global", undefined>;
};
const Switches: FC<SwitchesParams> = ({ t }) => {
  const { columnToSearch, columnSwitches, loading } = useSelector(
    (state: State) => state.DataTableReducer
  );
  const { columnsDataTable } = DataTableColumns();

  const { setColumnSearch, changeStatus, disableAll, selectAll } = SetActions();

  const names =
    columnsDataTable[0] !== undefined
      ? columnsDataTable.map((col) => col.field)
      : [""];

  const measureWording = t("Data.SwitchesValues.measure");
  const labelWording = t("Data.SwitchesValues.label");
  const dataWording = t("Data.SwitchesValues.data");



  const getColumnLabel = (col: any) => {
    if (col.id === "id") {
      return t("Data.table.Details");
    }
    if (col.id === "status") {
      return t("Data.table.Status");
    }
    return col.name;
  };

  const iconByType = (type: string) => {
    switch (type) {
      case "measure":
        return (
          <Tooltip title={measureWording}>
            <BiotechIcon sx={SwitchesStyles.iconStyle} />
          </Tooltip>
        );
      case "exported-measure":
        return (
          <Tooltip title={measureWording}>
            <BiotechIcon sx={SwitchesStyles.iconStyle} />
          </Tooltip>
        );
      case "label":
        return (
          <Tooltip title={labelWording}>
            <StickyNote2Icon sx={SwitchesStyles.iconStyle} />
          </Tooltip>
        );
      case "data":
        return (
          <Tooltip title={dataWording}>
            <AdjustIcon sx={SwitchesStyles.iconStyle} />
          </Tooltip>
        );
      default:
        return <AdjustIcon sx={SwitchesStyles.iconStyle} />;
    }
  };

  return (
    <FormControl sx={SwitchesStyles.FormControl} component="fieldset">
      <TextField
        value={columnToSearch}
        onChange={(e) => setColumnSearch(e.target.value)}
        placeholder={t("Data.Search_columns")}
        sx={SwitchesStyles.TextField}
      />
      <div style={SwitchesStyles.buttonsContainer}>
        <Button
          variant="text"
          sx={SwitchesStyles.Button}
          onClick={() => disableAll(names)}
        >
          {t("Data.Hide_all")}
        </Button>
        <Button
          variant="text"
          sx={SwitchesStyles.Button}
          onClick={() => selectAll(names)}
        >
          {t("Data.Show_all")}
        </Button>
      </div>
      <FormGroup
        sx={{
          ...SwitchesStyles.FormGroup,
          ...SwitchesStyles.switchesContainer,
        }}
      >
        {!loading ? (
          columnSwitches
            ?.filter((columns: ColumnSwitch) => {
              if (columnToSearch === "") {
                return columns;
              } else if (
                normalizeText(columns.name || "").includes(
                  normalizeText(columnToSearch)
                )
              ) {
                return columnToSearch;
              }
              return "";
            })
            .map((col: any) => {
              return (
                <FormControlLabel
                  key={col.id}
                  sx={SwitchesStyles.formControlLabel}
                  control={
                    <div style={SwitchesStyles.switchContainer}>
                      <Switch
                        size="small"
                        key={col.id}
                        checked={col.isSelected}
                        onChange={() => changeStatus(col.id)}
                        inputProps={{ "aria-label": "controlled" }}
                        name={col.name}
                      />

                      {getColumnLabel(col)}
                    </div>
                  }
                  label={<div>{iconByType(col.columnType)}</div>}
                />
              );
            })
        ) : (
          <div style={SwitchesStyles.circularProgressContainer}>
            <CircularProgress />
          </div>
        )}
      </FormGroup>
    </FormControl>
  );
};

export default Switches;
